import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, TreePine, Mail, Lock, Check, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import LanguageSelector from "../components/LanguageSelector";
import SnowLayer from "../components/SnowLayer";
import { Modal } from "../components/Modal";
import { MEDIA } from "../lib/media";
import { startCheckout } from "../lib/checkout";
import { LEGAL_LINKS } from "../i18n/legalLinks";

const track = (event, meta) => { api.post("/analytics/event", { event, meta: meta || {} }).catch(() => {}); };

// ---- Split-screen premium shell: cinematic image (left) + content (right) ----
export function AuthShell({ children }) {
  const { t } = useI18n();
  return (
    <div className="relative flex min-h-screen bg-xmas-navy">
      {/* Left cinematic panel (desktop) */}
      <div className="relative hidden w-1/2 overflow-hidden lg:block">
        <img src={MEDIA.authBg} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-xmas-navy/70 via-xmas-navy/45 to-xmas-navy/85" />
        <SnowLayer count={30} />
        <div className="absolute inset-0 flex flex-col justify-between p-10">
          <Link to="/" className="flex items-center gap-2" data-testid="auth-brand-logo">
            <TreePine className="h-6 w-6 text-xmas-gold" />
            <span className="font-display text-base font-bold tracking-widest text-xmas-snow">MY CHRISTMAS</span>
          </Link>
          <div>
            <p className="font-serif-elegant text-2xl italic text-xmas-champagne">{t("sales2.tagline_big")}</p>
            <p className="mt-2 max-w-sm text-sm text-xmas-snow/70">{t("auth.tagline_sub")}</p>
          </div>
        </div>
      </div>

      {/* Right content panel */}
      <div className="relative flex w-full flex-col overflow-hidden lg:w-1/2">
        <img src={MEDIA.hero} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25 lg:hidden" />
        <div className="absolute inset-0 bg-gradient-to-b from-xmas-navy/90 via-xmas-navy to-xmas-navy lg:hidden" />
        <div className="relative z-20 flex items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2 lg:invisible" data-testid="auth-brand-logo-mobile">
            <TreePine className="h-5 w-5 text-xmas-gold" />
            <span className="font-display text-sm font-bold tracking-widest text-xmas-snow">MY CHRISTMAS</span>
          </Link>
          <LanguageSelector compact />
        </div>
        <div className="relative z-10 flex flex-1 items-start justify-center overflow-y-auto px-5 pb-12 pt-2 sm:px-8">
          <div className="w-full max-w-md">{children}</div>
        </div>
      </div>
    </div>
  );
}

// ---- Shared pricing hook (market -> price; single global checkout) ----
function usePurchase() {
  const { lang } = useI18n();
  const [pricing, setPricing] = useState(null);
  const [market, setMarket] = useState("BR");
  const [showSoon, setShowSoon] = useState(false);
  useEffect(() => {
    api.get("/pricing").then(({ data }) => {
      setPricing(data);
      const saved = localStorage.getItem("mc_market");
      const initial = (saved && data.markets[saved]) ? saved
        : (data.detected && data.markets[data.detected]) ? data.detected : data.fallback;
      setMarket(initial);
    }).catch(() => {});
  }, []);
  const current = pricing?.markets?.[market];
  const buy = (source) => {
    const meta = { source, market, language: lang, landing_route: window.location.pathname,
      displayed_currency: current?.currency, displayed_price: current?.amount };
    track("click_buy", meta);
    startCheckout({ pricing, market, lang, meta, utm: {}, onSoon: () => setShowSoon(true) });
  };
  return { current, market, lang, buy, showSoon, setShowSoon };
}

// ---- LOGIN (premium) ----
export default function AuthPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  // Deep link preserved by ProtectedRoute (e.g. /admin/sales → login → back to /admin/sales).
  const returnTo = useLocation().state?.from;
  const { setUser } = useAuth();
  const { current, lang, buy, showSoon, setShowSoon } = usePurchase();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noAccess, setNoAccess] = useState(false);

  useEffect(() => { track("view_login"); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNoAccess(false);
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      setUser(data);
      const pendingInvite = localStorage.getItem("mc_pending_invite");
      if (pendingInvite) {
        localStorage.removeItem("mc_pending_invite");
        navigate(`/secret-santa/join/${pendingInvite}`, { replace: true });
        return;
      }
      navigate(typeof returnTo === "string" && /^\/(app|admin)(\/|\?|$)/.test(returnTo) ? returnTo : "/app", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error(t("login.locked"));
      else if (status === 403) toast.error(t("login.suspended"));
      else setNoAccess(true); // 401/404 → friendly no-enumeration message
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      {/* Login card */}
      <div className="glass rounded-3xl p-6 shadow-2xl sm:p-8" data-testid="login-card">
        <h1 className="font-display text-2xl font-bold text-xmas-snow sm:text-3xl" data-testid="login-title">{t("login.title")}</h1>
        <p className="mt-2 text-sm text-xmas-slate">{t("login.subtitle")}</p>

        {noAccess ? (
          <div className="mt-6 rounded-2xl border border-xmas-gold/30 bg-xmas-gold/10 p-5" data-testid="login-no-access-error">
            <p className="font-semibold text-xmas-champagne">{t("login.err_title")}</p>
            <p className="mt-1 text-sm text-xmas-slate">{t("login.err_text")}</p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button onClick={() => setNoAccess(false)} data-testid="login-retry-button" className="btn-gold flex-1 rounded-xl py-3 text-sm font-bold uppercase tracking-wider">{t("login.retry")}</button>
              <button onClick={() => buy("login_error")} data-testid="login-know-button" className="glass flex-1 rounded-xl py-3 text-sm font-semibold text-xmas-snow transition hover:border-xmas-gold/60">{t("login.know")}</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-xmas-slate" />
              <input data-testid="auth-email-input" required type="email" placeholder={t("auth.email")} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 py-3 pl-10 pr-4 text-sm text-xmas-snow outline-none transition focus:border-xmas-gold focus:ring-1 focus:ring-xmas-gold" />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-xmas-slate" />
              <input data-testid="auth-password-input" required type={showPw ? "text" : "password"} placeholder={t("auth.password")} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 py-3 pl-10 pr-11 text-sm text-xmas-snow outline-none transition focus:border-xmas-gold focus:ring-1 focus:ring-xmas-gold" />
              <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xmas-slate hover:text-xmas-gold" aria-label="toggle password">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="text-right">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-xmas-gold hover:underline">{t("auth.forgot")}</Link>
            </div>
            <button type="submit" disabled={loading} data-testid="auth-submit-button" className="btn-gold w-full rounded-xl py-3.5 text-sm font-bold uppercase tracking-wider disabled:opacity-60">
              {loading ? "..." : t("login.cta")}
            </button>
          </form>
        )}
      </div>

      {/* No-access / purchase section */}
      <div className="mt-6 rounded-3xl border border-xmas-gold/20 bg-xmas-navycard/60 p-6 sm:p-7" data-testid="login-purchase-section">
        <div className="mb-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-xmas-gold/15" />
          <span className="text-xs uppercase tracking-widest text-xmas-slate">{t("login.noaccess_title")}</span>
          <div className="h-px flex-1 bg-xmas-gold/15" />
        </div>
        <p className="text-sm text-xmas-slate">{t("login.noaccess_text")}</p>

        <ol className="mt-5 space-y-3">
          {(t("login.steps") || []).map((step, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`purchase-step-${i + 1}`}>
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-xmas-gold/20 font-mono-num text-xs font-bold text-xmas-gold">{i + 1}</span>
              <span className="text-sm text-xmas-snow">{step}</span>
            </li>
          ))}
        </ol>

        {current?.display && (
          <div className="mt-6 rounded-2xl border border-dashed border-xmas-gold/30 bg-white/5 p-5 text-center" data-testid="login-price-box">
            <p className="text-[11px] uppercase tracking-[0.25em] text-xmas-gold/80">{t("login.access_label")}</p>
            <p className="mt-1 font-display text-4xl font-black text-xmas-gold" data-testid="login-price-value">{current.display}</p>
            <p className="mt-1 text-xs text-xmas-slate">{t("login.one_time")}</p>
            <p className="mt-1 text-[11px] text-xmas-slate">{current.flag} {t("login.price_for", { country: current.name?.[lang] || "" })}</p>
          </div>
        )}

        <button onClick={() => buy("login_purchase")} data-testid="login-buy-button" className="btn-gold mt-5 flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold uppercase tracking-wider">
          {t("login.buy_cta")}
        </button>
      </div>

      <p className="mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs text-xmas-slate" data-testid="login-footer">
        <Link to="/refund-policy" data-testid="login-refund-link" className="underline-offset-4 transition hover:text-xmas-gold hover:underline">{(LEGAL_LINKS[lang] || LEGAL_LINKS.pt).refund}</Link><span className="text-xmas-gold/40">·</span>
        <Link to="/terms" data-testid="login-terms-link" className="underline-offset-4 transition hover:text-xmas-gold hover:underline">{(LEGAL_LINKS[lang] || LEGAL_LINKS.pt).terms}</Link><span className="text-xmas-gold/40">·</span>
        <Link to="/privacy" data-testid="login-privacy-link" className="underline-offset-4 transition hover:text-xmas-gold hover:underline">{(LEGAL_LINKS[lang] || LEGAL_LINKS.pt).privacy}</Link>
      </p>

      {createPortal(
        <Modal open={showSoon} onClose={() => setShowSoon(false)} title={t("checkout.soon_title")} testid="checkout-soon-modal">
          <p className="text-sm text-xmas-slate">{t("checkout.soon")}</p>
        </Modal>, document.body)}
    </AuthShell>
  );
}

// ---- FORGOT PASSWORD ----
export function ForgotPassword() {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email, language: lang }); setSent(true); }
    catch { setSent(true); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell>
      <div className="glass rounded-3xl p-7 shadow-2xl sm:p-9" data-testid="forgot-card">
        <h1 className="font-display text-2xl font-bold text-xmas-snow">{t("auth.forgot_title")}</h1>
        <p className="mt-2 text-sm text-xmas-slate">{t("auth.forgot_sub")}</p>
        {sent ? (
          <div className="mt-6 rounded-xl border border-xmas-gold/30 bg-xmas-gold/10 p-4 text-sm text-xmas-champagne" data-testid="forgot-sent-message">{t("auth.forgot_sent")}</div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <input data-testid="forgot-email-input" required type="email" placeholder={t("auth.email")} value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
            <button type="submit" disabled={loading} data-testid="forgot-submit-button" className="btn-gold w-full rounded-xl py-3.5 text-sm font-bold uppercase tracking-wider disabled:opacity-60">
              {loading ? "..." : t("auth.send_link")}
            </button>
          </form>
        )}
        <p className="mt-6 text-center text-sm"><Link to="/login" className="text-xmas-gold hover:underline">{t("cta.back")}</Link></p>
      </div>
    </AuthShell>
  );
}

// ---- CREATE / RESET PASSWORD (also activation) ----
export function ResetPassword({ activate = false }) {
  const { t, setLang } = useI18n();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [kept, setKept] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const token = new URLSearchParams(window.location.search).get("token");
  const [tokenState, setTokenState] = useState("checking");
  useEffect(() => {
    if (!token) { setTokenState("invalid"); return; }
    api.get(`/auth/reset-token/${encodeURIComponent(token)}`).then(({ data }) => {
      // The buyer's language (persisted at purchase) wins over browser/localStorage detection.
      if (data.valid && data.language) { localStorage.removeItem("mc_lang_manual"); setLang(data.language); }
      setTokenState(data.valid ? "ok" : data.reason);
    }).catch(() => setTokenState("ok"));
  }, [token, setLang]);

  const lenOk = password.length >= 6;
  const matchOk = password.length > 0 && password === confirm;
  if (tokenState !== "ok" && tokenState !== "checking") {
    return (
      <AuthShell>
        <div className="glass rounded-3xl p-8 text-center" data-testid="reset-link-invalid">
          <h1 className="font-display text-2xl text-xmas-snow">{t("ss.reveal_invalid")}</h1>
          <p className="mt-3 text-sm text-xmas-slate">{tokenState === "used" ? t("reset.link_used") : tokenState === "invalid" ? t("ss.reveal_invalid") : t("reset.link_expired")}</p>
          <Link to="/login" data-testid="reset-link-invalid-login" className="btn-gold mt-6 inline-block rounded-full px-6 py-3 text-xs font-bold uppercase tracking-wider">{t("login.title")}</Link>
        </div>
      </AuthShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!matchOk) { toast.error(t("create.mismatch")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data.session_switched === false) {
        // Another account (e.g. the admin) is logged in on this browser: keep it; only the buyer's password was created.
        setKept({ buyer: data.email, current: data.current_email });
      } else {
        setUser(data);
        if (data.language) setLang(data.language);
      }
      if (activate) {
        track("account_activated");
        track("first_login");
      }
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthShell>
        <div className="glass rounded-3xl p-8 text-center shadow-2xl" data-testid="create-success-card">
          <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-xmas-gold" />
          <h1 className="font-display text-2xl font-bold text-xmas-snow">{t("create.success_title")}</h1>
          <p className="mt-2 text-sm text-xmas-slate">{kept ? t("create.success_kept", { buyer: kept.buyer, current: kept.current }) : t("create.success_sub")}</p>
          <button onClick={() => navigate("/app", { replace: true })} data-testid="create-success-cta" className="btn-gold mt-7 w-full rounded-xl py-3.5 text-sm font-bold uppercase tracking-wider">
            {kept ? t("create.success_cta_kept") : t("create.success_cta")}
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="glass rounded-3xl p-7 shadow-2xl sm:p-9" data-testid="reset-card">
        <h1 className="font-display text-2xl font-bold text-xmas-snow">{t("create.title")}</h1>
        <p className="mt-2 text-sm text-xmas-slate">{t("create.sub")}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-xmas-slate" />
            <input data-testid="reset-password-input" required type={showPw ? "text" : "password"} placeholder={t("create.new")} value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 py-3 pl-10 pr-11 text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
            <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xmas-slate hover:text-xmas-gold" aria-label="toggle password">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-xmas-slate" />
            <input data-testid="reset-confirm-input" required type={showPw ? "text" : "password"} placeholder={t("create.confirm")} value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 py-3 pl-10 pr-4 text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
          </div>
          <ul className="space-y-1.5 text-xs">
            <li className={`flex items-center gap-2 ${lenOk ? "text-xmas-pine" : "text-xmas-slate"}`} data-testid="req-len">
              <Check className={`h-3.5 w-3.5 ${lenOk ? "opacity-100" : "opacity-30"}`} /> {t("create.req_len")}
            </li>
            <li className={`flex items-center gap-2 ${matchOk ? "text-xmas-pine" : "text-xmas-slate"}`} data-testid="req-match">
              <Check className={`h-3.5 w-3.5 ${matchOk ? "opacity-100" : "opacity-30"}`} /> {t("create.req_match")}
            </li>
          </ul>
          <button type="submit" disabled={loading || !token || !lenOk || !matchOk} data-testid="reset-submit-button" className="btn-gold w-full rounded-xl py-3.5 text-sm font-bold uppercase tracking-wider disabled:opacity-60">
            {loading ? "..." : t("create.cta")}
          </button>
        </form>
      </div>
    </AuthShell>
  );
}

// ---- PENDING PAYMENT ----
export function PendingPayment() {
  const { t } = useI18n();
  return (
    <AuthShell>
      <div className="glass rounded-3xl p-8 text-center shadow-2xl" data-testid="pending-card">
        <Clock className="mx-auto mb-4 h-14 w-14 text-xmas-gold" />
        <h1 className="font-display text-xl font-bold text-xmas-snow sm:text-2xl">{t("pending.title")}</h1>
        <p className="mt-3 text-sm text-xmas-slate">{t("pending.text")}</p>
        <Link to="/" data-testid="pending-back" className="btn-gold mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold uppercase tracking-wider">
          {t("pending.back")} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </AuthShell>
  );
}
